import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import ChatInterface from "@/components/ChatInterface";
import StoryCanvas from "@/components/StoryCanvas";
import BookSelector from "@/components/BookSelector";
import OnboardingModal from "@/components/OnboardingModal";
import PremiumVoiceRecorder from "@/components/PremiumVoiceRecorder";
import StoryCategoryPicker from "@/components/StoryCategoryPicker";
import BakingLoader from "@/components/BakingLoader";
import { LogOut, Leaf, Users, Share2, FileText, User, Pen, Info, X, ChevronRight, Book, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios to always send session token (skip admin routes)
axios.interceptors.request.use((config) => {
  const isAdminRoute = config.url?.includes('/api/admin');
  if (!isAdminRoute) {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [messages, setMessages] = useState([]);
  const [storyData, setStoryData] = useState({
    state: null,
    chapters: [],
    categorized_chapters: {},
    messages: [],
    active_book: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [activeBookId, setActiveBookId] = useState(null);
  const [writingStyle, setWritingStyle] = useState("standard");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [mobileCanvasOpen, setMobileCanvasOpen] = useState(false);
  const [showPremiumPlans, setShowPremiumPlans] = useState(false);
  const [showPremiumRecorder, setShowPremiumRecorder] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [creationPreset, setCreationPreset] = useState(null);
  const [showBaking, setShowBaking] = useState(false);
  const [bakeResult, setBakeResult] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
    } else {
      checkAuth();
    }
  }, [navigate, location.state]);

  // Fetch books list
  const fetchBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data.books);
      const active = response.data.books.find(b => b.is_active);
      if (active) {
        setActiveBookId(active.id);
        setWritingStyle(active.writing_style || "standard");
      }
      // Show onboarding for new users (only 1 book with 0 chapters and no onboarding done)
      const hasOnboarded = localStorage.getItem("onboarded");
      if (!hasOnboarded && response.data.books.length <= 1) {
        const chapterCount = response.data.books[0]?.chapter_count || 0;
        if (chapterCount === 0) {
          setShowOnboarding(true);
        }
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  }, []);

  // Fetch story for active book
  const fetchStory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/story`);
      setStoryData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching story:', error);
      return null;
    }
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadUserData = async () => {
      await fetchBooks();
      const data = await fetchStory();

      if (data?.messages && data.messages.length > 0) {
        const restored = data.messages.map(m => ({
          role: m.role,
          content: m.content,
          audio_url: m.audio_url,
          timestamp: m.timestamp
        }));

        try {
          const welcomeResp = await axios.get(`${API}/chat/welcome`);
          if (welcomeResp.data.is_returning) {
            restored.push({
              role: 'assistant',
              content: welcomeResp.data.message,
              timestamp: new Date().toISOString()
            });
          }
        } catch (e) { /* ignore */ }

        setMessages(restored);
      } else {
        try {
          const response = await axios.get(`${API}/chat/welcome`);
          setMessages([{
            role: 'assistant',
            content: response.data.message,
            timestamp: new Date().toISOString()
          }]);
        } catch (error) {
          console.error('Error fetching welcome:', error);
        }
      }
    };

    loadUserData();
  }, [isAuthenticated, fetchBooks, fetchStory]);

  // Switch book
  const handleBookSwitch = useCallback(async (bookId) => {
    setActiveBookId(bookId);
    setMessages([]);
    setStoryData({ state: null, chapters: [], categorized_chapters: {}, messages: [], active_book: null });

    // Reload data for new book
    const data = await fetchStory();
    await fetchBooks();

    if (data?.messages && data.messages.length > 0) {
      const restored = data.messages.map(m => ({
        role: m.role,
        content: m.content,
        audio_url: m.audio_url,
        timestamp: m.timestamp
      }));
      setMessages(restored);
    } else {
      try {
        const response = await axios.get(`${API}/chat/welcome`);
        setMessages([{
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString()
        }]);
      } catch (error) { /* ignore */ }
    }
  }, [fetchStory, fetchBooks]);

  const sendMessage = async (message, enableVoice = false) => {
    setIsLoading(true);
    try {
      const userMessage = {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);

      // 1) Send the text-only chat request (fast, no TTS to bloat payload)
      const response = await axios.post(`${API}/chat/message`, {
        message: message,
        enable_voice: false,
        book_id: activeBookId
      }, { timeout: 90_000 });

      const aiText = response.data.response;
      const aiMessage = {
        role: 'assistant',
        content: aiText,
        audio_url: null,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);

      if (response.data.chapter_added || response.data.analysis_complete) {
        await fetchStory();
        await fetchBooks();
      }

      if (response.data.book_renamed?.title) {
        const { title, genre } = response.data.book_renamed;
        toast.success(`Your book is now titled "${title}"${genre ? ` (${genre})` : ""}!`, { duration: 6000 });
      }

      // 2) If voice mode is on, generate TTS asynchronously and patch the message in place
      if (enableVoice && aiText) {
        axios.post(`${API}/tts/generate`, { text: aiText }, { timeout: 60_000 })
          .then((ttsRes) => {
            const audioUrl = ttsRes?.data?.audio_url;
            if (audioUrl) {
              setMessages((prev) => {
                // Patch the most recent assistant message that has no audio yet
                const next = [...prev];
                for (let i = next.length - 1; i >= 0; i--) {
                  if (next[i].role === 'assistant' && next[i].content === aiText && !next[i].audio_url) {
                    next[i] = { ...next[i], audio_url: audioUrl };
                    break;
                  }
                }
                return next;
              });
            }
          })
          .catch(() => { /* non-critical — user can still read text */ });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let detail = 'Please try again.';
      if (error.code === 'ECONNABORTED') {
        detail = 'Request timed out. Please check your connection and try again.';
      } else if (error.response?.status === 401) {
        detail = 'Your session expired. Please sign in again.';
      } else if (error.response?.status === 500) {
        detail = "Our server hit a snag. Try once more — if it persists, refresh the page.";
      } else if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
        const raw = error.response.data.detail;
        // Strip noisy raw KeyError / Python-exception strings the user shouldn't see
        const looksLikeRawError = /^['"][\w_]+['"]$/.test(raw.trim()) || raw.length < 4;
        detail = looksLikeRawError ? "Something went wrong saving your message. Please try again." : raw;
      } else if (error.message) {
        detail = error.message;
      }
      toast.error(`Failed to send message — ${detail}`);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImage = async (file, chapterId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/upload/image?chapter_id=${chapterId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await fetchStory();
      toast.success('Photo added!');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Failed to upload photo');
    }
  };

  const handleWritingStyleChange = async (style) => {
    if (!activeBookId) return;
    const previousStyle = writingStyle;
    if (style === previousStyle) return;

    // Persist the selection immediately (so subsequent new chapters pick it up too)
    setWritingStyle(style);
    try {
      await axios.put(`${API}/books/${activeBookId}/writing-style`, { writing_style: style });
    } catch { /* ignore */ }

    // If chapters exist, ask the user if they want to rewrite existing chapters in the new style
    const chapterCount = storyData?.chapters?.length || 0;
    if (chapterCount === 0) {
      toast.success(`Style set to ${style}. New chapters will use this voice.`);
      return;
    }

    const confirmed = window.confirm(
      `Rewrite all ${chapterCount} existing chapter${chapterCount === 1 ? "" : "s"} in "${style}" style? This will replace the current text (your raw inputs are preserved).`
    );
    if (!confirmed) {
      toast.info(`Style set to ${style}. Existing chapters were not rewritten.`);
      return;
    }

    const toastId = toast.loading(`Rewriting your story in ${style} style…`);
    try {
      await axios.post(`${API}/books/${activeBookId}/rewrite`, { writing_style: style });
      await fetchStory();
      toast.success(`All chapters rewritten in ${style} style!`, { id: toastId });
    } catch (e) {
      console.error("Rewrite failed:", e);
      // Roll back style selection on failure
      setWritingStyle(previousStyle);
      try { await axios.put(`${API}/books/${activeBookId}/writing-style`, { writing_style: previousStyle }); } catch {}
      toast.error("Failed to rewrite chapters. Please try again.", { id: toastId });
    }
  };

  const handleCategoryPick = (category) => {
    setShowCategoryPicker(false);
    if (category === "life") {
      setCreationPreset({
        title: "",
        genre: "Personal Growth",
        template: "freestyle",
        category: "life",
      });
    } else if (category === "short") {
      setCreationPreset({
        title: "",
        genre: "Other",
        template: "freestyle",
        category: "short",
      });
    }
  };

  const handleAfterBookCreate = async ({ id, category }) => {
    if (category === "short" && id) {
      let ok = true;
      try {
        await axios.post(`${API}/chat/onboarding`, { book_id: id, mode: "short", answers: {} });
      } catch (e) {
        ok = false;
      }
      if (ok) {
        toast.info("Short Story mode — the AI will stay laser-focused on this one incident.", { duration: 5000 });
      } else {
        toast.warning("Short Story created, but the AI persona couldn't be set. You may still proceed — the AI will default to general memoir mode.", { duration: 6000 });
      }
    }
  };

  const handleEndStory = async () => {
    if (!activeBookId) return;
    if (showBaking) return;
    setBakeResult(null);
    setShowBaking(true);
    try {
      const res = await axios.post(`${API}/books/${activeBookId}/bake`, {}, { timeout: 120_000 });
      setBakeResult(res.data);
      await fetchStory();
      await fetchBooks();
      toast.success("Your story is now fully baked and preserved for legacy.", { duration: 6000 });
    } catch (err) {
      setShowBaking(false);
      const detail = err.response?.data?.detail || "Something went wrong while baking. Please try again.";
      toast.error(detail);
    }
  };

  const closeBaking = () => {
    setShowBaking(false);
    setBakeResult(null);
  };


  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  const handleOnboardingComplete = async (data) => {
    setOnboardingData(data);
    setShowOnboarding(false);
    // Send onboarding data to inject into AI system prompt + receive a personalized greeting
    if (activeBookId && data) {
      try {
        const res = await axios.post(`${API}/chat/onboarding`, { book_id: activeBookId, ...data });
        const greeting = res?.data?.greeting;
        if (greeting) {
          // Replace any prior welcome with the personalized greeting (backend already cleared old assistant messages)
          setMessages([{ role: "assistant", content: greeting, timestamp: new Date().toISOString() }]);
        }
      } catch { /* non-critical */ }
    }
    toast.success("Got it! Your personal AI is ready — just reply to start your story.");
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-sans text-[#7A8A7A]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden bg-[#F8F6F1]">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal
          onComplete={handleOnboardingComplete}
          onClose={() => { setShowOnboarding(false); localStorage.setItem("onboarded", "true"); }}
        />
      )}
      {/* Unified top navigation bar — z-[70] so dropdowns always render above the mobile slide-in canvas (z-[60]) */}
      <div className="h-14 flex items-center justify-between px-3 sm:px-4 bg-white/70 backdrop-blur-md border-b border-[#D4C9B8]/30 z-[70] flex-shrink-0 relative">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="home-button">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center">
            <Leaf className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-serif text-sm font-medium text-[#2D3A2D] hidden xs:inline sm:inline">My Story</span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink min-w-0">
          {/* Writing Style — hidden on small mobile to free up space; shown sm+ */}
          <div className="hidden sm:flex items-center gap-1.5 relative group/style">
            <span className="text-[9px] font-sans text-[#9FB89F] uppercase tracking-wider hidden lg:inline">Style:</span>
            <select
              value={writingStyle}
              onChange={(e) => handleWritingStyleChange(e.target.value)}
              className="px-2 py-1.5 rounded-full text-[11px] font-sans bg-white/80 text-[#7A8A7A] border border-[#D4C9B8]/50 focus:outline-none focus:ring-1 focus:ring-[#7A9B7E]/40"
              data-testid="writing-style-select"
            >
              <option value="standard">Standard</option>
              <option value="simple">Simple English</option>
              <option value="humorous">Humorous</option>
            </select>
            <div className="relative">
              <Info className="w-3.5 h-3.5 text-[#9FB89F] cursor-help" data-testid="style-info-icon" />
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#D4C9B8]/40 p-3 hidden group-hover/style:block z-50" data-testid="style-tooltip">
                <p className="font-sans text-[10px] font-semibold text-[#2D3A2D] mb-1.5">Styles of Writing</p>
                <p className="font-sans text-[9px] text-[#7A8A7A] mb-1"><b>Standard:</b> Warm, authentic, human tone</p>
                <p className="font-sans text-[9px] text-[#7A8A7A] mb-1"><b>Simple:</b> Plain English, easy to read</p>
                <p className="font-sans text-[9px] text-[#7A8A7A]"><b>Humorous:</b> Witty, sarcastic, entertaining</p>
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/occasions")} className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-sans bg-white/80 text-[#7A8A7A] border border-[#D4C9B8]/50 hover:bg-[#F8F6F1] transition-all" data-testid="occasions-link">
            <Users className="w-3 h-3 text-[#7A9B7E]" /> Events
          </button>
          <button onClick={() => { const ab = books.find(b => b.id === activeBookId); if (ab) navigate(`/export/${ab.id}`); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-sans bg-[#4A5D4A] text-white hover:bg-[#2D3A2D] transition-all" data-testid="export-button">
            <FileText className="w-3 h-3" /> Export
          </button>
          <BookSelector
            books={books}
            activeBookId={activeBookId}
            onSwitch={handleBookSwitch}
            onRefresh={fetchBooks}
            writingStyle={writingStyle}
            onWritingStyleChange={handleWritingStyleChange}
            onOpenOccasions={() => navigate("/occasions")}
            onRequestCreate={() => setShowCategoryPicker(true)}
            creationPreset={creationPreset}
            onCreationPresetConsumed={() => setCreationPreset(null)}
            onAfterBookCreate={handleAfterBookCreate}
          />
          <button onClick={() => navigate("/profile")} className="p-2 hover:bg-[#E5D4C1]/50 rounded-full transition-colors" title="Profile" data-testid="profile-button">
            <User className="w-4 h-4 text-[#7A8A7A]" />
          </button>
          <button onClick={handleLogout} className="p-2 hover:bg-[#E5D4C1]/50 rounded-full transition-colors" title="Logout" data-testid="logout-button">
            <LogOut className="w-4 h-4 text-[#7A8A7A]" />
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left: Chat — full screen on mobile, fixed-width on desktop */}
        <div className="w-full md:w-[400px] lg:w-[450px] md:border-r border-[#D4C9B8]/30 flex flex-col h-full">
          <ChatInterface
            messages={messages}
            onSendMessage={sendMessage}
            onUploadImage={uploadImage}
            isLoading={isLoading}
            user={user}
            onOpenPremiumPlans={() => setShowPremiumRecorder(true)}
          />
        </div>

        {/* Right: Story — desktop side panel, mobile full-screen slide-in BELOW the navbar */}
        <div
          className={`fixed inset-x-0 top-14 bottom-0 z-[60] bg-[#F8F6F1] flex flex-col md:static md:top-auto md:flex md:flex-1 md:h-full md:overflow-hidden md:bg-transparent md:translate-x-0 transition-transform duration-300 ease-out ${
            mobileCanvasOpen ? "translate-x-0" : "translate-x-full md:translate-x-0"
          }`}
          data-testid="story-canvas-panel"
        >
          {/* Mobile-only close bar — flex-shrink-0 so it never collapses; opaque so canvas chrome can't bleed through */}
          <div
            className="md:hidden flex-shrink-0 flex items-center justify-between px-4 py-3 bg-white shadow-sm border-b border-[#D4C9B8]/40"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <span className="font-serif text-base text-[#2D3A2D]">Your Story</span>
            <button
              onClick={() => setMobileCanvasOpen(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4A5D4A] text-white text-xs font-sans active:scale-95"
              data-testid="close-canvas-mobile"
            >
              <X className="w-3.5 h-3.5" /> Close
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden md:flex-1">
            <StoryCanvas
              storyData={storyData}
              onRefresh={() => { fetchStory(); fetchBooks(); }}
              onUploadImage={uploadImage}
              activeBook={books.find(b => b.id === activeBookId)}
              onEndStory={handleEndStory}
            />
          </div>
        </div>

        {/* Mobile-only floating "View Story" pill */}
        {!mobileCanvasOpen && (
          <button
            onClick={() => setMobileCanvasOpen(true)}
            className="md:hidden fixed right-4 z-30 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#4A5D4A] text-white shadow-xl shadow-[#4A5D4A]/30 hover:bg-[#2D3A2D] transition-all active:scale-95"
            style={{ bottom: "max(6rem, calc(6rem + env(safe-area-inset-bottom)))" }}
            data-testid="view-story-fab"
          >
            <Book className="w-4 h-4" />
            <span className="font-sans text-sm font-medium">View Story</span>
            {storyData?.chapters?.length > 0 && (
              <span className="bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full">
                {storyData.chapters.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Premium Recorder — Whisper-1 multi-language voice (live) */}
      {showPremiumRecorder && (
        <PremiumVoiceRecorder
          onClose={() => setShowPremiumRecorder(false)}
          onTranscript={(text) => {
            sendMessage(text, false);
          }}
        />
      )}

      {/* Premium Plans Modal — reserved for when Razorpay is wired */}
      {showPremiumPlans && (
        <PremiumPlansModal onClose={() => setShowPremiumPlans(false)} />
      )}

      {/* Story Category Picker — intercepts "New Memoir" clicks */}
      {showCategoryPicker && (
        <StoryCategoryPicker
          onSelect={handleCategoryPick}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}

      {/* Baking Loader — final narrative review */}
      {showBaking && (
        <BakingLoader result={bakeResult} onClose={closeBaking} />
      )}
    </div>
  );
}

const PREMIUM_TIERS = [
  { id: "starter", price: 100, minutes: 50, label: "Starter", desc: "Try it out" },
  { id: "standard", price: 200, minutes: 120, label: "Standard", desc: "Most popular", featured: true },
  { id: "creator", price: 400, minutes: 300, label: "Creator", desc: "For serious memoirs" },
  { id: "legacy", price: 700, minutes: 600, label: "Legacy", desc: "Best value" },
];

function PremiumPlansModal({ onClose }) {
  const [selected, setSelected] = useState("standard");

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="premium-plans-modal">
      <div
        className="bg-[#F8F6F1] w-full md:max-w-lg md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#2D3A2D] to-[#4A5D4A] text-white p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition"
            data-testid="close-premium-modal"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-amber-300/90 bg-amber-300/15 px-2 py-0.5 rounded-full">Premium</span>
            <span className="text-[10px] font-sans text-white/60">Coming soon</span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-1">Multi-Language Voice</h2>
          <p className="font-sans text-sm text-white/80">Speak in Hindi, Tamil, Bengali, Marathi & 50+ more — powered by Whisper.</p>
        </div>

        {/* Plans */}
        <div className="p-5 space-y-2.5 max-h-[55vh] overflow-y-auto">
          {PREMIUM_TIERS.map((t) => {
            const isSelected = selected === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className={`w-full text-left rounded-2xl p-4 border-2 transition-all relative ${
                  isSelected ? "border-[#7A9B7E] bg-white shadow-md" : "border-[#D4C9B8]/50 bg-white/60 hover:bg-white"
                }`}
                data-testid={`premium-tier-${t.id}`}
              >
                {t.featured && (
                  <span className="absolute -top-2 left-4 text-[9px] font-sans uppercase tracking-wider bg-[#C67B5C] text-white px-2 py-0.5 rounded-full">
                    {t.desc}
                  </span>
                )}
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-serif text-lg text-[#2D3A2D]">{t.label}</span>
                  <span className="font-serif text-2xl font-bold text-[#2D3A2D]">₹{t.price}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-sans text-[#7A8A7A]">
                  <span>{t.minutes} minutes of voice</span>
                  <span>₹{(t.price / t.minutes).toFixed(2)}/min</span>
                </div>
                {isSelected && (
                  <div className="mt-2 pt-2 border-t border-[#D4C9B8]/40 flex items-center gap-1.5 text-[11px] text-[#7A9B7E] font-sans">
                    <Check className="w-3 h-3" /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="p-5 border-t border-[#D4C9B8]/30 bg-white/80 backdrop-blur">
          <button
            onClick={() => {
              toast.info("Premium voice is launching soon — we'll notify you the moment it's live!", { duration: 5000 });
              onClose();
            }}
            className="w-full py-3 rounded-full bg-[#4A5D4A] text-white font-sans font-medium text-sm hover:bg-[#2D3A2D] transition disabled:opacity-50"
            data-testid="premium-cta"
          >
            Notify me when this launches
          </button>
          <p className="text-center text-[10px] font-sans text-[#9FB89F] mt-2">No card needed yet — preview-only pricing</p>
        </div>
      </div>
    </div>
  );
}

export default App;
